import React from "react";
import { get } from "lodash";
import PropTypes from "prop-types";
import { LoadMoreStoriesBase } from "@quintype/components";
import { WebEngage, GA, PGAD } from "../../../integrations";
import { LoadMoreButton } from "../../atoms/read-more-link";
import ResponsiveImageWithFallback from "../../atoms/responsive-image-with-fallback";
import { generateImageSources } from "../../utils/utils";
import { FIELDS_FOR_LISTING_PAGE, PAGE_TYPE } from "../../../constants";

import styles from "./styles.m.css";
import { PGAdwithAd } from "../../rows/ad-free";
import Media from "react-media";
import LatestNewsStories from "../../atoms/latest-news-stories";
import SpecialBundleCard from "../../atoms/special-bundle-card";
import SmallStoryCardCurve from "../../atoms/small-story-card-with-curve";

const StoryList = ({ stories, topicspage }) =>
  stories.length > 0 ? (
    <React.Fragment>
      {stories.map((story, index) => {
        return (
          <React.Fragment key={story.id}>
            <SmallStoryCardCurve story={story} className={styles["story-card"]} />
          </React.Fragment>
        );
      })}
    </React.Fragment>
  ) : (
    <h3 className={styles["no-results"]}>No stories found.</h3>
  );

StoryList.propTypes = {
  stories: PropTypes.arrayOf(SmallStoryCardCurve.propTypes.story),
  topicspage: PropTypes.bool
};

const StoryListBase = ({ stories, loading, onLoadMore, noMoreStories, topicspage = true }) => (
  <React.Fragment>
    <StoryList stories={stories} />
    {!noMoreStories &&
      stories.length >= 8 && (
        <div className={styles["load-more-wrap"]}>
          <LoadMoreButton
            text={loading ? "LOADING..." : " மேலும் படிக்க"}
            disabled={loading}
            onClick={onLoadMore}
            className={styles["stories-load-more"]}
          />
        </div>
      )}
  </React.Fragment>
);

export { StoryListBase };

StoryListBase.propTypes = {
  stories: PropTypes.arrayOf(SmallStoryCardCurve.propTypes.story),
  loading: PropTypes.bool,
  onLoadMore: PropTypes.func,
  noMoreStories: PropTypes.bool,
  topicspage: PropTypes.bool
};

const SpecialTopicPage = ({ data }) => {
  const entityName = get(data, ["entity", "tamil-name"]);
  const entityContent = get(data, ["entity", "content"]);
  const entityImage = get(data, ["entity", "photo", 0]);
  const [isTopicOpen, setIsTopicOpen] = React.useState(false);
  React.useEffect(() => {
    WebEngage.registerPageView();
    GA.registerPageView(PAGE_TYPE.TOPIC_PAGE, {
      entity: get(data, ["entity"], {})
    });
    PGAD.loadScript();
  }, []);
  // const handleTopicOpenToggleClick = () => {
  //   setIsTopicOpen(!isTopicOpen);
  // };

  const topicContentRef = React.useRef(null);

  const handleTopicOpenToggleClick = () => {
    const isCollapsing = isTopicOpen; // If true, user is collapsing content
    setIsTopicOpen(!isTopicOpen);

    if (isCollapsing && topicContentRef.current) {
      // Wait for the collapse animation to finish (if any)
      setTimeout(() => {
        topicContentRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 20);
    }
  };

  return (
    <div className={`${styles["special-topic-page-container"]}`}>
      <div className={`${styles["main-story-grid"]} container`}>
        <div className={styles["left-side-panel"]}>
          <div className={styles.base}>
            <div>
              <div className={`${styles.header}`}>
                {entityName && <h1 className={`hidden-desktop ${styles["topic-headline"]}`}>{entityName}</h1>}
                <div className={styles["topic-content"]}>
                  {entityImage && (
                    <div className={styles["topic-image-wrapper"]}>
                      <ResponsiveImageWithFallback
                        className={styles["topic-image"]}
                        slug={entityImage.key}
                        metadata={{}}
                        alt={entityName}
                        imgParams={{ auto: ["format", "compress"] }}
                        sources={generateImageSources({
                          aspectRatio: [3, 4],
                          screenWidthCoverage: 0.3
                        })}
                      />
                    </div>
                  )}
                  {entityName && <h1 className={`hidden-mobile ${styles["topic-headline"]}`}>{entityName}</h1>}
                  {entityContent && (
                    <div
                      ref={topicContentRef}
                      className={`${styles["topic-content-text"]} ${isTopicOpen ? styles["is-open"] : "not-open"}`}
                    >
                      <div dangerouslySetInnerHTML={{ __html: entityContent }} />
                      <LoadMoreButton
                        className={styles["topic-load-more-button"]}
                        text={isTopicOpen ? "குறைவாக படிக்க" : "மேலும் படிக்க"}
                        onClick={handleTopicOpenToggleClick}
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className={styles["story-list"]}>
                <LoadMoreStoriesBase
                  api="/api/v1/advanced-search"
                  apiResponseTransformer={response => response.items}
                  template={StoryListBase}
                  numStoriesToLoad={20}
                  data={data}
                  params={{ q: entityName, "content-types": "story" }}
                  fields={FIELDS_FOR_LISTING_PAGE}
                />
              </div>
              <div className={styles["ad-container"]}>
                <PGAdwithAd
                  adId={`${styles["ad"]}`}
                  adSize={[[336, 280], [300, 250]]}
                  minHeight={280}
                  adDivId="div-ad-desk-3849069-169028051743-4"
                  class=""
                />
              </div>
            </div>
          </div>
        </div>
        <div>
          <div className={styles["right-side-panel"]}>
            <aside className={`${styles["right-sidebar"]} ${styles.sidebar}`}>
              <Media query="(min-width:768px)">
                <PGAdwithAd
                  adId={`Vikatan2023_Desktop_AP_RightATF_336x280`}
                  adSize={[[336, 280], [300, 250]]}
                  minHeight={280}
                  adDivId={`div-ad-desk-3849069-1690281136-2_${"52d3fe06-4ff8-4e9e-84e1-59c040c0490a"}`}
                  class="ad_300_250"
                  story={"goldpage"}
                  // config={this.props.config}
                />
              </Media>
            </aside>
            <LatestNewsStories collectionSlug="latest-news-collection" title="லேட்டஸ்ட் செய்திகள்" />
            <Media query="(min-width:768px)">
              <PGAdwithAd
                adId={`Vikatan2023_Desktop_AP_RightMID_336x280`}
                adSize={[[336, 280], [300, 250]]}
                minHeight={280}
                adDivId={`div-ad-desk-3849069-1690281136-1_${123}`}
                class="ad_300_250"
                story={"goldpage"}
                // config={this.props.config}
              />
            </Media>

            <SpecialBundleCard className="feed-page" config={""} />
          </div>
          <Media query="(min-width:768px)">
            <div className={`${styles["ad-halfpage"]} hidden-mobile`}>
              <PGAdwithAd
                adId={`Vikatan2023_Desktop_AP_RHS_300x600`}
                adSize={[[300, 600]]}
                minHeight={600}
                adDivId={`div-ad-desk-3849069-1690281136-0_${1234}`}
                class="ad-300x600"
                story={"story"}
                config={""}
              />
            </div>
          </Media>
        </div>
      </div>
    </div>
  );
};

export { SpecialTopicPage };

SpecialTopicPage.propTypes = {
  data: PropTypes.shape({
    entity: PropTypes.shape({
      name: PropTypes.string,
      properties: PropTypes.shape({
        content: PropTypes.string,
        photo: PropTypes.arrayOf(
          PropTypes.shape({
            key: PropTypes.string
          })
        )
      })
    }),
    stories: PropTypes.arrayOf(SmallStoryCardCurve.propTypes.story)
  })
};
